import { useEffect, useState } from 'react';
import { apiKeysApi } from '../api';
import { Plus, Trash2, EyeOff, BarChart3, Copy, Shield, Clock, Activity } from 'lucide-react';

interface ApiKeyData {
  id: string;
  name: string;
  keyPrefix: string;
  isActive: boolean;
  permissions: any;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  user?: { id: string; firstName: string; lastName: string; email: string };
  requestCount?: number;
}

interface StatsData {
  key: any;
  period: any;
  summary: { totalRequests: number; avgDuration: number };
  requestsByDay: { date: string; count: number }[];
  requestsByStatus: { statusCode: number; count: number }[];
  requestsByMethod: { method: string; count: number }[];
  recentLogs: any[];
}

export default function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKeyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [statsKey, setStatsKey] = useState<string | null>(null);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [statsDays, setStatsDays] = useState(7);
  const [globalStats, setGlobalStats] = useState<any>(null);
  const [copied, setCopied] = useState(false);

  const loadKeys = async () => {
    try {
      const res = await apiKeysApi.list();
      setKeys(res.data);
    } catch (e) {
      console.error('Load API keys error:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadGlobalStats = async () => {
    try {
      const res = await apiKeysApi.globalStats(statsDays);
      setGlobalStats(res.data);
    } catch (e) {
      console.error('Load global stats error:', e);
    }
  };

  useEffect(() => { loadKeys(); loadGlobalStats(); }, []);
  useEffect(() => { loadGlobalStats(); }, [statsDays]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    try {
      const res = await apiKeysApi.create({ name: newKeyName, expiresAt: newKeyExpiry || undefined });
      setCreatedKey(res.data.key);
      setNewKeyName('');
      setNewKeyExpiry('');
      setShowCreate(false);
      loadKeys();
      loadGlobalStats();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка создания ключа');
    }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm('Деактивировать API-ключ?')) return;
    try {
      await apiKeysApi.revoke(id);
      loadKeys();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка деактивации');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить API-ключ навсегда?')) return;
    try {
      await apiKeysApi.remove(id);
      loadKeys();
      loadGlobalStats();
      if (statsKey === id) { setStatsKey(null); setStats(null); }
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка удаления');
    }
  };

  const loadStats = async (id: string) => {
    if (statsKey === id) { setStatsKey(null); setStats(null); return; }
    setStatsKey(id);
    try {
      const res = await apiKeysApi.stats(id, statsDays);
      setStats(res.data);
    } catch (e) {
      console.error('Load stats error:', e);
    }
  };

  useEffect(() => {
    if (statsKey) loadStats(statsKey);
  }, [statsDays]);

  const copyKey = () => {
    if (createdKey) { navigator.clipboard.writeText(createdKey); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  const formatDate = (d: string | null) => d ? new Date(d).toLocaleString('ru-RU') : '—';
  const maxDayCount = stats ? Math.max(...stats.requestsByDay.map(d => d.count), 1) : 1;

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Управление API-ключами</h2>
        <button className="btn btn-primary" onClick={() => { setShowCreate(true); setCreatedKey(null); }}>
          <Plus size={18} /> Создать ключ
        </button>
      </div>

      {/* Global Stats */}
      {globalStats && (
        <div className="dashboard-grid" style={{ marginBottom: 24 }}>
          <div className="widget">
            <div className="widget-icon"><Shield size={22} /></div>
            <div className="widget-label">Всего ключей</div>
            <div className="widget-value">{globalStats.totalKeys}</div>
          </div>
          <div className="widget">
            <div className="widget-icon"><Activity size={22} /></div>
            <div className="widget-label">Активных ключей</div>
            <div className="widget-value">{globalStats.activeKeys}</div>
          </div>
          <div className="widget">
            <div className="widget-icon"><BarChart3 size={22} /></div>
            <div className="widget-label">Запросов ({statsDays} дн.)</div>
            <div className="widget-value">{globalStats.totalRequests}</div>
          </div>
          <div className="widget">
            <div className="widget-icon"><Clock size={22} /></div>
            <div className="widget-label">Период</div>
            <div style={{ marginTop: 8 }}>
              <select className="form-select" value={statsDays} onChange={e => setStatsDays(parseInt(e.target.value))} style={{ width: '100%', fontSize: 13 }}>
                <option value={1}>1 день</option>
                <option value={7}>7 дней</option>
                <option value={30}>30 дней</option>
                <option value={90}>90 дней</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Created Key Banner */}
      {createdKey && (
        <div className="card" style={{ marginBottom: 24, border: '2px solid var(--color-success)' }}>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Shield size={24} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--color-success)' }}>API-ключ создан!</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                Сохраните этот ключ — он больше не будет показан.
              </div>
              <code style={{ display: 'block', padding: '10px 14px', background: 'var(--color-bg)', borderRadius: 8, fontSize: 13, wordBreak: 'break-all', border: '1px solid var(--color-border)' }}>
                {createdKey}
              </code>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={copyKey}>
              <Copy size={14} /> {copied ? 'Скопировано!' : 'Копировать'}
            </button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Новый API-ключ</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Название *</label>
                <input className="form-input" value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder="Например: Интеграция с 1С" />
              </div>
              <div className="form-group">
                <label className="form-label">Срок действия (необязательно)</label>
                <input className="form-input" type="datetime-local" value={newKeyExpiry} onChange={e => setNewKeyExpiry(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Отмена</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={!newKeyName.trim()}>Создать</button>
            </div>
          </div>
        </div>
      )}

      {/* Keys Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">API-ключи</h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Ключ</th>
                    <th>Статус</th>
                    <th>Запросов</th>
                    <th>Последний запрос</th>
                    <th>Создан</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>
                      API-ключи не созданы
                    </td></tr>
                  )}
                  {keys.map(k => (
                    <tr key={k.id} style={statsKey === k.id ? { background: 'var(--color-primary-soft)' } : undefined}>
                      <td style={{ fontWeight: 600 }}>{k.name}</td>
                      <td><code style={{ fontSize: 12, background: 'var(--color-bg)', padding: '2px 6px', borderRadius: 4 }}>{k.keyPrefix}</code></td>
                      <td>
                        <span className={`badge ${k.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {k.isActive ? 'Активен' : 'Отозван'}
                        </span>
                      </td>
                      <td>{k.requestCount ?? 0}</td>
                      <td className="text-muted" style={{ fontSize: 12 }}>{formatDate(k.lastUsedAt)}</td>
                      <td className="text-muted" style={{ fontSize: 12 }}>{formatDate(k.createdAt)}</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="header-icon-btn" style={{ width: 32, height: 32 }} onClick={() => loadStats(k.id)} title="Статистика">
                            <BarChart3 size={14} />
                          </button>
                          {k.isActive && (
                            <button className="header-icon-btn" style={{ width: 32, height: 32, color: 'var(--color-warning)' }} onClick={() => handleRevoke(k.id)} title="Отозвать">
                              <EyeOff size={14} />
                            </button>
                          )}
                          <button className="header-icon-btn" style={{ width: 32, height: 32, color: 'var(--color-danger)' }} onClick={() => handleDelete(k.id)} title="Удалить">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Stats Panel */}
      {statsKey && stats && (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-header">
            <h3 className="card-title">Статистика: {stats.key.name}</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => { setStatsKey(null); setStats(null); }}>Закрыть</button>
          </div>
          <div className="card-body">
            <div className="dashboard-grid" style={{ marginBottom: 24 }}>
              <div className="widget">
                <div className="widget-label">Всего запросов</div>
                <div className="widget-value">{stats.summary.totalRequests}</div>
              </div>
              <div className="widget">
                <div className="widget-label">Среднее время ответа</div>
                <div className="widget-value">{stats.summary.avgDuration}ms</div>
              </div>
            </div>

            {/* Requests by Day Chart */}
            {stats.requestsByDay.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Запросы по дням</h4>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120, padding: '0 8px' }}>
                  {stats.requestsByDay.map(d => (
                    <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{d.count}</span>
                      <div style={{ width: '100%', background: 'var(--color-primary)', borderRadius: 4, height: `${(d.count / maxDayCount) * 80}px`, minHeight: 4 }} />
                      <span style={{ fontSize: 9, color: 'var(--color-text-muted)' }}>{new Date(d.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* By Status & Method */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
              <div>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>По статус-кодам</h4>
                {stats.requestsByStatus.map(s => (
                  <div key={s.statusCode} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--color-border-soft)', fontSize: 13 }}>
                    <span className={`badge ${s.statusCode < 400 ? 'badge-success' : 'badge-danger'}`}>{s.statusCode}</span>
                    <span style={{ fontWeight: 600 }}>{s.count}</span>
                  </div>
                ))}
              </div>
              <div>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>По методам</h4>
                {stats.requestsByMethod.map(m => (
                  <div key={m.method} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--color-border-soft)', fontSize: 13 }}>
                    <span className="badge badge-info">{m.method}</span>
                    <span style={{ fontWeight: 600 }}>{m.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Logs */}
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Последние запросы</h4>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Метод</th>
                    <th>Путь</th>
                    <th>Статус</th>
                    <th>Время</th>
                    <th>IP</th>
                    <th>Дата</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentLogs.map((log: any) => (
                    <tr key={log.id}>
                      <td><span className="badge badge-info">{log.method}</span></td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{log.path}</td>
                      <td><span className={`badge ${log.statusCode < 400 ? 'badge-success' : 'badge-danger'}`}>{log.statusCode}</span></td>
                      <td>{log.duration}ms</td>
                      <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{log.ipAddress || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{new Date(log.createdAt).toLocaleString('ru-RU')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
